import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Button,
} from '@mui/material';
import {
  Assessment,
  CheckCircle,
  Warning,
  Info,
  Close,
  Download,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { ComplianceResult } from '../types/api';
import { apiCall } from '../utils/api';

const ResultsPage: React.FC = () => {
  const [regulationIds, setRegulationIds] = useState<string[]>([]);
  const [selectedRegulation, setSelectedRegulation] = useState<string>('');
  const [result, setResult] = useState<ComplianceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    fetchRegulationIds();
  }, []);

  useEffect(() => {
    if (selectedRegulation) {
      fetchComplianceResult(selectedRegulation);
    }
  }, [selectedRegulation]);

  const fetchRegulationIds = async () => {
    try {
      const response = await apiCall('/api/results/regulation-ids');
      const data = await response.json();
      setRegulationIds(data.regulation_ids);
      
      if (data.regulation_ids.length > 0) {
        setSelectedRegulation(data.regulation_ids[0]);
      }
    } catch (error) {
      console.error('Error fetching regulation IDs:', error);
      setError('Failed to load regulation IDs');
    }
  };

  const fetchComplianceResult = async (regulationId: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await apiCall(`/api/results/${regulationId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch result');
      }

      const data = await response.json();
      console.log('Fetched compliance result:', {
        regulationId,
        hasReferences: !!(data.references && data.references.trim()),
        hasRegulationText: !!(data.regulation_text && data.regulation_text.trim()),
        referencesLength: data.references?.length,
        regulationTextLength: data.regulation_text?.length,
        data
      });
      setResult(data);
    } catch (error) {
      console.error('Error fetching compliance result:', error);
      setError('Failed to load compliance result');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadComplianceMatrix = async () => {
    setDownloadLoading(true);
    setError('');
    
    try {
      const response = await apiCall('/api/results/compliance-matrix/download');
      
      if (!response.ok) {
        throw new Error('Failed to download CSV file');
      }
      
      // Create blob from response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = 'compliance_matrix.csv';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading CSV file:', error);
      setError('Failed to download compliance matrix CSV file');
    } finally {
      setDownloadLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    const statusLower = status.toLowerCase();
    
    // Check for non-compliant first (red cross)
    if (statusLower.includes('non-compliant') || statusLower.includes('non compliant') || statusLower.includes('fail')) {
      return <Close color="error" />;
    }
    
    // Check for compliant (green tick)
    if (statusLower.includes('compliant') || statusLower.includes('pass')) {
      return <CheckCircle color="success" />;
    }
    
    // Warning states (orange warning)
    if (statusLower.includes('warning') || statusLower.includes('partial')) {
      return <Warning color="warning" />;
    }
    
    // Default info icon
    return <Info color="info" />;
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    
    // Check for non-compliant first (red)
    if (statusLower.includes('non-compliant') || statusLower.includes('non compliant') || statusLower.includes('fail')) {
      return 'error';
    }
    
    // Check for compliant (green)
    if (statusLower.includes('compliant') || statusLower.includes('pass')) {
      return 'success';
    }
    
    // Warning states (orange)
    if (statusLower.includes('warning') || statusLower.includes('partial')) {
      return 'warning';
    }
    
    // Default info color
    return 'info';
  };

  if (regulationIds.length === 0 && !error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Results Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Run the compliance analysis process to generate results.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Compliance Analysis Results
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review the compliance analysis results for each regulation point.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {regulationIds.length > 0 && (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: '0 0 33%' }}>
            <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" gutterBottom>
                Regulation Points
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Regulation</InputLabel>
                <Select
                  value={selectedRegulation}
                  label="Select Regulation"
                  onChange={(e) => setSelectedRegulation(e.target.value)}
                >
                  {regulationIds.map((id) => (
                    <MenuItem key={id} value={id}>
                      {id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Total Regulations: {regulationIds.length}
                </Typography>

                <Button
                  variant="contained"
                  startIcon={downloadLoading ? <CircularProgress size={16} /> : <Download />}
                  onClick={downloadComplianceMatrix}
                  disabled={downloadLoading}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  {downloadLoading ? 'Downloading...' : 'Download CSV Matrix'}
                </Button>
              </Box>

              {/* Regulation Text Section */}
              {result && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Regulation Text
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {result.regulation_text && result.regulation_text.trim() && result.regulation_text !== "No regulation text found" ? (
                    <Box sx={{
                      bgcolor: 'grey.50',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      '& p': { mb: 1, fontFamily: 'monospace', fontSize: '0.75rem' },
                      '& ul, & ol': { pl: 2, mb: 1, fontFamily: 'monospace', fontSize: '0.75rem' },
                      '& li': { mb: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' },
                      '& h1, & h2, & h3, & h4, & h5, & h6': { mb: 1, mt: 1, fontFamily: 'monospace', fontSize: '0.8rem' }
                    }}>
                      <ReactMarkdown>{result.regulation_text}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No regulation text available for this regulation point.
                    </Alert>
                  )}
                </Box>
              )}
            </Paper>
          </Box>

          <Box sx={{ flex: 1 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading compliance result...
                </Typography>
              </Box>
            ) : result ? (
              <Box>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {getStatusIcon(result.status)}
                      <Typography variant="h5" sx={{ ml: 1, flexGrow: 1 }}>
                        {result.regulation_id}
                      </Typography>
                      <Chip
                        label={result.status}
                        color={getStatusColor(result.status) as any}
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography variant="h6" gutterBottom>
                      Compliance Status
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {result.status}
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Analysis Explanation
                    </Typography>
                    <Box sx={{ 
                      '& p': { mb: 1 },
                      '& ul, & ol': { pl: 2, mb: 1 },
                      '& li': { mb: 0.5 },
                      '& h1, & h2, & h3, & h4, & h5, & h6': { mb: 1, mt: 1 }
                    }}>
                      <ReactMarkdown>{result.explanation}</ReactMarkdown>
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Document References
                    </Typography>
                    {result.references && result.references.trim() && result.references !== "No references found" ? (
                      <Box sx={{
                        '& p': { mb: 1 },
                        '& ul, & ol': { pl: 2, mb: 1 },
                        '& li': { mb: 0.5 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': { mb: 1, mt: 1 }
                      }}>
                        <ReactMarkdown>{result.references}</ReactMarkdown>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        No document references available for this regulation point.
                      </Alert>
                    )}
                  </CardContent>
                </Card>

              </Box>
            ) : selectedRegulation && (
              <Alert severity="warning">
                No result found for regulation: {selectedRegulation}
              </Alert>
            )}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default ResultsPage;